import { HomeOutlined, RightOutlined } from "@ant-design/icons";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useState, useEffect, useRef } from "react";
import { Button, Checkbox, Modal, Spin, Switch, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { useGetSectors } from "../hooks/useGetSectors";
import { AppButton } from "components/button/AppButton";
import { routePath } from "config/routeMgt/routePath";
import { useQueryClient } from "react-query";
import { useCreateJob } from "../hooks/useCreateJob";
import { openNotification } from "utils/notification";
import { IJobsProps } from "../types";
import SuccessModal from "components/layout/components/SuccessModal";
import WarningModal from "components/layout/components/WarningModal";
import { useJobFormStore } from "../hooks/useJobFormStore";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { formatJobYearsLabel } from "../utils/formatJobYearsLabel";
import { useExtractInformationJD } from "../hooks/useExtractInformationJD";
import {
  useListingSocialPosts,
  useSocialPreferences,
} from "../hooks/useSocialAutoPost";

const socialStatusColor = (status: string) => {
  switch (status) {
    case "posted":
      return "success";
    case "failed":
      return "error";
    case "skipped":
      return "default";
    case "processing":
      return "processing";
    default:
      return "warning";
  }
};

export const JobCreationPreview = () => {
  const { token, userInfo } = useGetUserInfo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateJob();
  const { mutateAsync: extractJD } = useExtractInformationJD();
  const { data: socialPreferences } = useSocialPreferences(token);

  const {
    title,
    company_name,
    sector_id,
    location_country,
    location_city,
    level,
    type,
    employment_type,
    experience,
    language,
    expiryDate,
    description,
    descriptionInputType,
    skills,
    jobId,
    resetForm,
    social_auto_post,
    social_platforms,
    setSocialAutoPost,
    setSocialPlatforms,
    hydrateSocialDefaults,
  } = useJobFormStore();

  const { data: sectorData } = useGetSectors();
  const sector = sectorData?.find((item) => item.id === sector_id);

  const formattedDate = formatDate(expiryDate);

  const [isJobCreatedModalVisible, setJobCreatedModalVisible] = useState(false);
  const [createdJobId, setCreatedJobId] = useState<string | null>(null);
  const [createdJobSlug, setCreatedJobSlug] = useState<string | null>(null);
  const [trackSocialPosts, setTrackSocialPosts] = useState(false);
  const [postedPlatforms, setPostedPlatforms] = useState<
    Array<"linkedin" | "facebook" | "x">
  >([]);
  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const [isSubmitModalVisible, setSubmitModalVisible] = useState(false);
  const [content, setContent] = useState("Job published successfully");
  const [formattedDescription, setFormattedDescription] = useState<string>("");
  const [finalDescription, setFinalDescription] = useState<string>("");
  const [isFormatting, setIsFormatting] = useState(false);

  const { data: socialPostsData } = useListingSocialPosts(
    token,
    createdJobId,
    trackSocialPosts && isJobCreatedModalVisible,
  );

  useEffect(() => {
    if (socialPreferences) {
      hydrateSocialDefaults({
        auto_post_enabled_by_default:
          socialPreferences.auto_post_enabled_by_default,
        default_platforms: socialPreferences.default_platforms,
      });
    }
  }, [socialPreferences, hydrateSocialDefaults]);

  const hasFormattedRef = useRef(false);

  useEffect(() => {
    const formatDescriptionWithAI = async () => {
      if (!description) {
        setFormattedDescription("No description provided");
        setFinalDescription("No description provided");
        return;
      }

      if (hasFormattedRef.current) {
        setFormattedDescription(description);
        setFinalDescription(description);
        return;
      }

      const isFromUpload =
        descriptionInputType === "upload" ||
        description.includes("font-family: Arial, sans-serif") ||
        description.includes("line-height: 1.6") ||
        description.includes("<ul") ||
        description.includes("<li") ||
        description.includes("<p>") ||
        description.includes("<h4");

      if (isFromUpload) {
        setFormattedDescription(description);
        setFinalDescription(description);
        hasFormattedRef.current = true;
        return;
      }

      const isAlreadyFormatted =
        description.includes("<div") ||
        description.includes("<p>") ||
        description.includes("<ul>") ||
        description.includes("font-family");

      if (isAlreadyFormatted) {
        setFormattedDescription(description);
        setFinalDescription(description);
        hasFormattedRef.current = true;
        return;
      }

      setIsFormatting(true);
      try {
        const apiResponse = await extractJD({
          jd: description,
          token,
        });

        if (apiResponse.description) {
          setFormattedDescription(apiResponse.description);
          setFinalDescription(apiResponse.description);
          hasFormattedRef.current = true;
        } else {
          const formatted = formatDescriptionManually(description);
          setFormattedDescription(formatted);
          setFinalDescription(formatted);
        }
      } catch (error) {
        console.error("AI formatting failed, using manual formatting:", error);
        const formatted = formatDescriptionManually(description);
        setFormattedDescription(formatted);
        setFinalDescription(formatted);
      } finally {
        setIsFormatting(false);
      }
    };

    formatDescriptionWithAI();
  }, [description, token, descriptionInputType]);

  const handleSubmit = (status: "draft" | "published" | "private") => {
    const descriptionToSubmit = finalDescription || description;

    if (!title || !company_name || !descriptionToSubmit) {
      openNotification({
        title: "Incomplete Form",
        description: "Please fill in all required fields",
        state: "error",
      });
      return;
    }

    const props: IJobsProps = {
      recruiter_id: userInfo?.recruiter?.id,
      token,
      userInfo,
      title: title,
      sector_id: sector_id,
      company_name: company_name,
      employment_type: employment_type,
      type: type,
      level: level,
      language: language,
      expiry_date: expiryDate ? expiryDate.format("YYYY-MM-DD") : "",
      years_of_experience:
        experience !== undefined &&
          experience !== null &&
          String(experience).trim() !== ""
          ? String(experience).trim()
          : "",
      description: descriptionToSubmit,
      location_id: 1,
      location_city: location_city,
      location_country: location_country,
      id: jobId,
      skills: skills.map((skill) => skill.name),
      status: status,
      social_auto_post: status === "published" ? social_auto_post : false,
      social_platforms:
        status === "published" && social_auto_post ? social_platforms : [],
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        const listing = res?.data?.data;
        setCreatedJobId(listing?.id ?? null);
        setCreatedJobSlug(listing?.slug ?? null);
        setTrackSocialPosts(
          status === "published" && Boolean(social_auto_post),
        );
        setPostedPlatforms(
          status === "published" && social_auto_post ? social_platforms : [],
        );

        if (status === "draft") {
          setContent("Job saved to draft successfully");
        } else if (status === "private") {
          setContent(
            "Job saved as private. This job will not be visible on the public portal.",
          );
        } else if (social_auto_post) {
          const platformsLabel = social_platforms
            .map((p) =>
              p === "linkedin" ? "LinkedIn" : p === "facebook" ? "Facebook" : "X",
            )
            .join(" & ");
          setContent(
            `Job published successfully. Auto-posting to Arno ${platformsLabel} platforms is in progress.`,
          );
        } else {
          setContent("Job published successfully");
        }

        setJobCreatedModalVisible(true);
        resetForm();
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
      },
    });
  };

  const handlePublish = () => {
    setSubmitModalVisible(true);
  };

  const handleCancel = () => {
    setCancelModalVisible(true);
  };

  const handleConfirmCancel = () => {
    resetForm();
    navigate(routePath.jobs);
  };

  const formatDescriptionManually = (html: string): string => {
    if (!html) return "";

    if (html.includes("<ul") && html.includes("<li")) {
      return html;
    }

    let text = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\s{2,}/g, " ")
      .trim();

    let sentences: string[] = [];

    let tempText = text;
    tempText = tempText.replace(/Q\d+\./g, (match) =>
      match.replace(/\./g, "___DOT___"),
    );

    const parts = tempText.split(/(?<=\.)\s+/);

    for (let part of parts) {
      part = part.replace(/___DOT___/g, ".");
      if (part.trim()) {
        sentences.push(part.trim());
      }
    }

    if (sentences.length <= 1 && text.includes(". ")) {
      const manualParts = text.split(". ");
      for (let part of manualParts) {
        if (part.trim()) {
          let cleanPart = part.trim();
          if (
            !cleanPart.endsWith(".") &&
            !cleanPart.endsWith("?") &&
            !cleanPart.endsWith("!")
          ) {
            cleanPart += ".";
          }
          sentences.push(cleanPart);
        }
      }
    }

    if (sentences.length === 0) {
      sentences = [text];
    }

    const result: string[] = [];
    let inBulletList = false;
    let inSubBulletList = false;

    for (let i = 0; i < sentences.length; i++) {
      let line = sentences[i].trim();
      if (!line) continue;

      const startsWithBullet = /^[•·▪◦○●◆■\-*+]\s/.test(line);
      const bulletMatch = line.match(/^([•·▪◦○●◆■\-*+])\s+(.+)/);

      const isSubBullet = /^[○◦o]\s/.test(line);

      if (bulletMatch || startsWithBullet || isSubBullet) {
        let content = line;
        let isSub = isSubBullet;

        if (bulletMatch) {
          content = bulletMatch[2];
        } else if (isSubBullet) {
          content = line.replace(/^[○◦o]\s*/, "");
        } else {
          content = line.replace(/^[•·▪◦○●◆■\-*+]\s*/, "");
        }

        if (isSub && !inSubBulletList) {
          if (inBulletList) {
            result.push("</ul>");
            inBulletList = false;
          }
          result.push('<ul class="list-disc pl-10 mb-3">');
          inSubBulletList = true;
        } else if (!isSub && !inBulletList) {
          if (inSubBulletList) {
            result.push("</ul>");
            inSubBulletList = false;
          }
          result.push('<ul class="list-disc pl-6 mb-3">');
          inBulletList = true;
        }

        if (inSubBulletList) {
          result.push(`<li class="ml-6 mb-1 text-gray-700">${content}</li>`);
        } else {
          result.push(`<li class="ml-6 mb-1 text-gray-700">${content}</li>`);
        }
        continue;
      }

      if (inBulletList) {
        result.push("</ul>");
        inBulletList = false;
      }
      if (inSubBulletList) {
        result.push("</ul>");
        inSubBulletList = false;
      }

      const isHeader =
        /^(Job Summary|Principal Duties and Responsibilities|Competencies|Skills|Attributes|Knowledge|Key Performance Indicators|Minimum Qualifications|Other Requirements|Education|Experience|Language|Supervised By|Grade Level|Division|Job Position|Finance and General Operations|Treasury and Investment Operations|Investee Company Support|Account Management)/i.test(
          line,
        );

      if (isHeader) {
        const isSubHeader =
          /^(Finance and General Operations|Treasury and Investment Operations|Investee Company Support|Account Management)/i.test(
            line,
          );
        if (isSubHeader) {
          result.push(
            `<h5 class="font-semibold mt-4 mb-2 text-gray-700">${line}</h5>`,
          );
        } else {
          result.push(
            `<h4 class="font-semibold mt-4 mb-2 text-gray-800">${line}</h4>`,
          );
        }
        continue;
      }

      if (line.includes(":") && !isHeader) {
        const colonIndex = line.indexOf(":");
        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        if (key.length < 40 && value.length > 0) {
          result.push(
            `<p class="mb-2 text-gray-700 leading-relaxed"><span class="font-semibold text-gray-800">${key}:</span> ${value}</p>`,
          );
          continue;
        }
      }

      const qMatch = line.match(/^(Q\d+\.)\s*(.+)/);
      if (qMatch) {
        result.push(
          `<p class="mb-2 text-gray-700 leading-relaxed"><span class="font-semibold text-gray-800">${qMatch[1]}</span> ${qMatch[2]}</p>`,
        );
        continue;
      }

      result.push(`<p class="mb-2 text-gray-700 leading-relaxed">${line}</p>`);
    }

    if (inBulletList) {
      result.push("</ul>");
    }
    if (inSubBulletList) {
      result.push("</ul>");
    }

    return result.join("\n");
  };

  const getDisplayDescription = () => {
    let desc = formattedDescription || description || "No description provided";

    desc = desc.replace(/<table>.*?Job Position:.*?<\/table>/gi, "");
    desc = desc.replace(/<p>.*?Job Position:.*?<\/p>/gi, "");
    desc = desc.replace(/<p>.*?Reports to:.*?<\/p>/gi, "");
    desc = desc.replace(/<p>.*?Grade Level:.*?<\/p>/gi, "");
    desc = desc.replace(/<p>.*?Division:.*?<\/p>/gi, "");

    const patternsToRemove = [
      /<p><strong>Job Position:<\/strong>[^<]*<\/p>/gi,
      /<p><strong>Supervised By:<\/strong>[^<]*<\/p>/gi,
      /<p><strong>Grade Level:<\/strong>[^<]*<\/p>/gi,
      /<p><strong>Supervises:<\/strong>[^<]*<\/p>/gi,
      /<p><strong>Division:<\/strong>[^<]*<\/p>/gi,
    ];

    for (const pattern of patternsToRemove) {
      desc = desc.replace(pattern, "");
    }

    const jobSummaryIndex = desc.indexOf("Job Summary");
    if (jobSummaryIndex !== -1) {
      desc = desc.substring(jobSummaryIndex);
    }

    if (!desc.includes("<ul") || !desc.includes("<li")) {
      desc = formatDescriptionManually(desc);
    }

    return desc;
  };

  const platformLabel = (platform: string) => {
    if (platform === "linkedin") return "LinkedIn";
    if (platform === "facebook") return "Facebook";
    if (platform === "x" || platform === "twitter") return "X";
    return platform;
  };

  return (
    <DashboardLayout>
      <Navbar title="Jobs" description="View and Manage Jobs" />

      <div>
        <h3 className="text-sm flex items-center gap-1 text-gray-700">
          <HomeOutlined className="text-gray-500" />
          <span>Jobs&nbsp;</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;Job Details</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;{`${title} (${company_name})`}</span>
        </h3>
      </div>

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-4 mt-4">
          <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div>
              <h3 className="text-base font-bold">{title}</h3>
              <h4 className="inline-flex items-center gap-1.5 mt-2 bg-[#7F56D9] text-white px-2.5 py-1 rounded-full text-xs font-semibold">
                <i className="ri-building-4-line text-white text-sm"></i>
                {company_name}
              </h4>
            </div>
            <div className="relative">
              <div>
                <ul className="p-2 divide-y divide-gray-200 w-full">
                  <li className="grid grid-cols-1 sm:grid-cols-4 sm:divide-x sm:divide-gray-200/70 py-4">
                    <div className="flex flex-col w-full py-1 pl-6 text-sm font-semibold">
                      <div className="flex items-center space-x-2 text-gray-800">
                        <span className="rounded-full w-[32px] h-[32px] bg-gray-100 flex items-center justify-center">
                          <i className="ri-map-pin-line text-lg text-blue-500"></i>
                        </span>
                        <div className="flex flex-col">
                          <div className="text-gray-400 text-xs">Location</div>
                          <div className="text-gray-500 text-base capitalize">{`${location_city}, ${location_country}`}</div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-gray-100 flex items-center justify-center">
                        <i className="ri-briefcase-line text-lg text-blue-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">
                          Employment Type
                        </div>
                        <div className="text-gray-500 text-base capitalize">
                          {employment_type}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-gray-100 flex items-center justify-center">
                        <i className="ri-building-line text-lg text-green-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Work Model</div>
                        <div className="text-gray-500 text-base capitalize">
                          {type}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-purple-50 flex items-center justify-center">
                        <i className="ri-award-line text-lg text-purple-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">
                          Years of experience
                        </div>
                        <div className="text-gray-500 text-base capitalize">
                          {formatJobYearsLabel(experience) || "ΓÇö"}
                        </div>
                      </div>
                    </div>
                  </li>
                  <li className="grid grid-cols-1 sm:grid-cols-4 sm:divide-x sm:divide-gray-200/70 py-4">
                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-blue-50 flex items-center justify-center">
                        <i className="ri-briefcase-4-line text-lg text-blue-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Industry</div>
                        <div className="text-gray-500 text-base capitalize">
                          {sector?.name}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-green-50 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-lg text-green-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">Job Status</div>
                        <div className="text-gray-500 text-base capitalize">
                          Preview
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center w-full py-2 pl-6 text-sm font-semibold space-x-3">
                      <span className="rounded-full w-[32px] h-[32px] bg-orange-50 flex items-center justify-center">
                        <i className="ri-calendar-event-line text-lg text-orange-500"></i>
                      </span>
                      <div className="flex flex-col">
                        <div className="text-gray-400 text-xs">
                          Application Deadline
                        </div>
                        <div className="text-gray-500 text-base capitalize">
                          {formattedDate}
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div className="relative">
              <div>
                <div className="flex flex-col w-full py-1 pl-6 text-sm font-semibold">
                  <div className="flex items-start space-x-2 text-gray-800">
                    <div className="flex flex-col w-full">
                      <div className="text-gray-600 text-base mb-3">
                        Job Description
                      </div>
                      <Spin spinning={isFormatting}>
                        <div
                          className="job-description-content"
                          dangerouslySetInnerHTML={{
                            __html: getDisplayDescription(),
                          }}
                        />
                      </Spin>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div className="relative">
              <div>
                <div className="flex flex-col w-full py-1 pl-6 text-sm font-semibold">
                  <div className="flex items-center space-x-2 text-gray-800">
                    <div className="flex flex-col">
                      <div className="text-gray-600 text-base">Competency</div>
                      <div className="flex flex-wrap gap-4 mt-3 capitalize">
                        {skills.map((skill, index) => (
                          <span
                            key={index}
                            className="bg-[#E3E5FF] text-[#4B5563] text-xs font-medium px-4 py-3 rounded-md capitalize"
                          >
                            {skill.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-lg shadow-md h-full p-6 lg:col-span-6">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-gray-800 font-semibold">
                    Auto-post to Arno social media
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    When enabled, publishing this job queues posts to Arno&apos;s
                    Facebook, and/or X accounts.
                  </p>
                </div>
                <Switch
                  checked={social_auto_post}
                  onChange={(checked) => setSocialAutoPost(checked)}
                />
              </div>
              {social_auto_post && (
                <Checkbox.Group
                  value={social_platforms}
                  onChange={(values) =>
                    setSocialPlatforms(
                      values as Array<"linkedin" | "facebook" | "x">,
                    )
                  }
                  options={[
                    { label: "LinkedIn", value: "linkedin" },
                    { label: "Facebook", value: "facebook" },
                    { label: "X", value: "x" },
                  ]}
                />
              )}
            </div>
          </div> */}

          <div className="p-3 w-full mx-auto lg:col-span-6">
            <div className="col-span-4 flex justify-between items-center mt-4">
              <div data-tour="back-job">
                <Button
                  onClick={() => navigate(-1)}
                  className="mr-2 bg-white text-blue-900 rounded border border-gray-500 p-1.5 px-8"
                >
                  Back
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </div>
              <div className="flex gap-2" data-tour="submit-job">
                <Button
                  onClick={() => handleSubmit("draft")}
                  loading={isLoading}
                  className="px-6"
                >
                  Save as Draft
                </Button>
                <Button
                  onClick={() => handleSubmit("private")}
                  loading={isLoading}
                  className="px-6"
                >
                  Save as Private
                </Button>
                <AppButton
                  label="Publish"
                  handleClick={handlePublish}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>

        <Modal
          title="Publish Job"
          open={isSubmitModalVisible}
          onCancel={() => setSubmitModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setSubmitModalVisible(false)}>
              Cancel
            </Button>,
            <AppButton
              key="publish"
              label="Publish Now"
              handleClick={() => {
                handleSubmit("published");
                setSubmitModalVisible(false);
              }}
              isLoading={isLoading}
            />,
          ]}
        >
          <p>
            Are you sure you want to publish this job? It will be visible to
            candidates immediately.
          </p>
          {social_auto_post && social_platforms.length > 0 && (
            <p className="mt-3 text-sm text-gray-600">
              Arno will also auto-post to{" "}
              {social_platforms.map(platformLabel).join(" and ")}.
            </p>
          )}
        </Modal>

        <SuccessModal
          open={isJobCreatedModalVisible}
          content={
            <div className="space-y-3">
              <p>{content}</p>
              {trackSocialPosts && (
                <div className="flex flex-wrap justify-center gap-2">
                  {(socialPostsData?.posts?.length
                    ? socialPostsData.posts
                    : postedPlatforms.map((platform) => ({
                      id: platform,
                      platform,
                      status: "pending" as const,
                      external_url: null,
                    }))
                  ).map((post) => (
                    <Tag
                      key={post.id}
                      color={socialStatusColor(post.status)}
                      className="px-3 py-1"
                    >
                      {platformLabel(post.platform)}: {post.status}
                      {post.external_url ? (
                        <>
                          {" "}
                          ΓÇö{" "}
                          <a
                            href={post.external_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            view
                          </a>
                        </>
                      ) : null}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          }
          onClose={() => {
            setJobCreatedModalVisible(false);
            setTrackSocialPosts(false);
            setPostedPlatforms([]);
            navigate(routePath.jobs);
          }}
        />

        <WarningModal
          open={isCancelModalVisible}
          title="Cancel progress"
          content={<>Once cancelled, progress would not be saved.</>}
          onConfirm={handleConfirmCancel}
          onCancel={() => setCancelModalVisible(false)}
          confirmText="Cancel"
          cancelText="Back"
        />
      </div>
    </DashboardLayout>
  );
};
